// frontend/src/modules/expenses/api.ts
import { api, getToken } from '../../core/api-client';
import { downloadFile } from '../payroll/api';
import type {
  Expense, ExpensePage, CreateExpenseInput, UpdateExpenseInput, ExpenseListParams,
  Category, CreateCategoryInput,
  Attachment, ApprovalChain, ChainLevel, ExpenseApproval, ReimburseResult,
  ExpenseApprovalPolicy, CreatePolicyInput, UpdatePolicyInput,
} from './types';

function expensesQuery(params: ExpenseListParams, includePaging: boolean): string {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.employee_id) qs.set('employee_id', params.employee_id);
  if (params.category_id) qs.set('category_id', params.category_id);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (includePaging) {
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
  }
  const tail = qs.toString();
  return tail ? `?${tail}` : '';
}

// ---- Expenses ----
export const expensesApi = {
  /** Backward-compatible: returns just the rows. */
  list: (params: ExpenseListParams = {}) =>
    api<{ rows: Expense[] }>(`/api/expenses${expensesQuery(params, true)}`).then((r) => r.rows),

  /** Paginated list with total count for pagination controls. */
  listPage: (params: ExpenseListParams = {}) =>
    api<ExpensePage>(`/api/expenses${expensesQuery(params, true)}`),

  /** Build the CSV export URL (filters honored; pagination ignored). */
  exportUrl: (params: ExpenseListParams = {}) =>
    `/api/expenses/export${expensesQuery(params, false)}`,

  /**
   * Fetch the CSV as an authed blob and trigger a browser download. Uses the
   * shared `downloadFile` helper (same one payroll/invoicing/reports use) so
   * a blocked export (e.g. a trial tenant's 402 `trial_export_blocked`)
   * surfaces the backend's actual message instead of a bare status code.
   */
  downloadCsv: (params: ExpenseListParams = {}): Promise<void> =>
    downloadFile(expensesApi.exportUrl(params), `expenses-${new Date().toISOString().slice(0, 10)}.csv`),
  get: (id: string) =>
    api<{ expense: Expense }>(`/api/expenses/${id}`).then((r) => r.expense),
  create: (input: CreateExpenseInput) =>
    api<{ expense: Expense }>('/api/expenses', { method: 'POST', body: input }).then((r) => r.expense),
  update: (id: string, input: UpdateExpenseInput) =>
    api<{ expense: Expense }>(`/api/expenses/${id}`, { method: 'PATCH', body: input }).then((r) => r.expense),
  remove: (id: string) =>
    api<void>(`/api/expenses/${id}`, { method: 'DELETE' }),

  submit: (id: string) =>
    api<{ expense: Expense }>(`/api/expenses/${id}/submit`, { method: 'POST', body: {} }).then((r) => r.expense),
  approve: (id: string, note?: string) =>
    api<{ expense: Expense }>(`/api/expenses/${id}/approve`, { method: 'POST', body: { note: note ?? null } }).then((r) => r.expense),
  reject: (id: string, reason: string) =>
    api<{ expense: Expense }>(`/api/expenses/${id}/reject`, { method: 'POST', body: { reason } }).then((r) => r.expense),
  markReimbursed: (id: string) =>
    api<{ expense: Expense }>(`/api/expenses/${id}/mark-reimbursed`, { method: 'POST', body: {} }).then((r) => r.expense),

  /** Full audit trail of approval decisions for an expense. */
  approvals: (id: string) =>
    api<{ rows: ExpenseApproval[] }>(`/api/expenses/${id}/approvals`).then((r) => r.rows),

  /** Submitted expenses awaiting the current user's decision (chain-routed). */
  pendingApprovals: () =>
    api<{ rows: Expense[] }>('/api/expenses/pending-approvals').then((r) => r.rows),

  /** Push approved+reimbursable expenses into a draft payroll run. */
  reimburse: (input: { expense_ids: string[]; run_id: string; employee_id: string }) =>
    api<ReimburseResult>('/api/expenses/reimburse', { method: 'POST', body: input }),

  // Attachments — multipart, so we hand-roll the fetch and reuse the JWT
  // pattern from core/api-client. Everything still routes through this file so
  // pages never call fetch() directly.
  uploadAttachment: async (expenseId: string, file: File): Promise<Attachment> => {
    const token = getToken();
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/expenses/${expenseId}/attachments`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const text = await res.text();
    let json: any = null;
    if (text) { try { json = JSON.parse(text); } catch { /* leave null */ } }
    if (!res.ok) {
      const msg = json?.error?.message ?? `Upload failed (${res.status})`;
      throw new Error(msg);
    }
    return json.attachment as Attachment;
  },
  removeAttachment: (expenseId: string, attachmentId: string) =>
    api<void>(`/api/expenses/${expenseId}/attachments/${attachmentId}`, { method: 'DELETE' }),
};

// ---- Categories ----
export const expenseCategoriesApi = {
  list: () =>
    api<{ rows: Category[] }>('/api/expenses/categories').then((r) => r.rows),
  create: (input: CreateCategoryInput) =>
    api<{ category: Category }>('/api/expenses/categories', { method: 'POST', body: input }).then((r) => r.category),
  update: (id: string, input: Partial<CreateCategoryInput> & { is_active?: boolean }) =>
    api<{ category: Category }>(`/api/expenses/categories/${id}`, { method: 'PATCH', body: input }).then((r) => r.category),
  remove: (id: string) =>
    api<void>(`/api/expenses/categories/${id}`, { method: 'DELETE' }),
};

// ---- Approval chains ----
export const approvalChainsApi = {
  list: () =>
    api<{ rows: ApprovalChain[] }>('/api/expenses/approval-chains').then((r) => r.rows),
  create: (input: { name: string; levels: ChainLevel[] }) =>
    api<{ chain: ApprovalChain }>('/api/expenses/approval-chains', { method: 'POST', body: input }).then((r) => r.chain),
  update: (id: string, input: { name?: string; levels?: ChainLevel[]; is_active?: boolean }) =>
    api<{ chain: ApprovalChain }>(`/api/expenses/approval-chains/${id}`, { method: 'PATCH', body: input }).then((r) => r.chain),
  remove: (id: string) =>
    api<void>(`/api/expenses/approval-chains/${id}`, { method: 'DELETE' }),
};

// ---- Approval policies (migration 076) ----
// Every route here — reads included — is gated on `expenses.approve` server
// side: a policy decides whether the CFO ever sees the 9,000 claim, so editing
// one is an admin-tier action.
export const approvalPoliciesApi = {
  list: (includeInactive = false) =>
    api<{ rows: ExpenseApprovalPolicy[] }>(
      `/api/expenses/approval-policies${includeInactive ? '?include_inactive=true' : ''}`,
    ).then((r) => r.rows),
  create: (input: CreatePolicyInput) =>
    api<{ policy: ExpenseApprovalPolicy }>('/api/expenses/approval-policies', { method: 'POST', body: input })
      .then((r) => r.policy),
  update: (id: string, input: UpdatePolicyInput) =>
    api<{ policy: ExpenseApprovalPolicy }>(`/api/expenses/approval-policies/${id}`, { method: 'PATCH', body: input })
      .then((r) => r.policy),
  remove: (id: string) =>
    api<void>(`/api/expenses/approval-policies/${id}`, { method: 'DELETE' }),
};
